import { create } from "zustand"

/**
 * In-memory upload progress per photo/detail id.
 *
 * Kept in Zustand (not Dexie) on purpose — axios `onUploadProgress` fires many times per
 * second and writing to IndexedDB on every tick would saturate the DB and trigger
 * cascades of `useLiveQuery` re-renders across the overview. The store is keyed by photo
 * or detail id; the same shape works for both.
 *
 * Lifecycle: entry is created when an upload starts (set to 0), updated as bytes flow,
 * and deleted in the uploader's `finally` block — regardless of success or failure.
 * Persistent end-states (uploaded, failed) live in Dexie.
 */
type UploadProgressState = {
  progress: Record<string, number>
  setProgress: (id: string, value: number) => void
  clearProgress: (id: string) => void
}

export const useUploadProgressStore = create<UploadProgressState>((set) => ({
  progress: {},
  setProgress: (id, value) =>
    set((state) => ({
      progress: { ...state.progress, [id]: value },
    })),
  clearProgress: (id) =>
    set((state) => {
      if (!(id in state.progress)) return state
      const next = { ...state.progress }
      delete next[id]
      return { progress: next }
    }),
}))
